import styled from 'styled-components';
import { Button } from 'antd';
import { IToken } from 'store/ducks/globalTypes';
import { themesList } from 'constants/themesList';

const DARK_THEMES = Object.keys(themesList).filter(
  (t) =>
    (themesList[t as keyof typeof themesList] as any).needsBlackText === true
) as (keyof typeof themesList)[];

const isDarkTheme = (scheme?: string) =>
  DARK_THEMES.includes(scheme as keyof typeof themesList);

const getDarkThemeTextColor = (token: any, scheme?: string) =>
  isDarkTheme(scheme) ? '#000000 !important' : token.colorText;

export const EditorContainer = styled.div<IToken & { scheme?: string }>`
  border: 1px solid ${({ token }) => token.colorBorderSecondary};
  border-radius: 4px;
  overflow: hidden;
  background: ${({ token }) => token.colorBgBase};
  display: flex;
  flex-direction: column;
  min-height: 200px;
  position: relative;
  width: 400px;
  height: 300px;
`;

export const ToolbarContainer = styled.div<IToken>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px;
  background: ${({ token }) => token.colorBgContainer};
  border-bottom: 1px solid ${({ token }) => token.colorBorder};
  flex-wrap: wrap;
`;

export const SelectWrapper = styled.div`
  width: 100px;
`;

export const ToolbarButton = styled.button<
  IToken & {
    disabled?: boolean;
    isPrimary?: boolean;
  }
>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid
    ${({ isPrimary, token }) =>
      isPrimary ? token?.colorPrimary : token?.colorBorder};
  background: ${({ isPrimary, token }) =>
    isPrimary ? token?.colorPrimaryBg : token?.colorBgBase};
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: ${({ isPrimary, token }) =>
    isPrimary ? token?.colorPrimary : token?.colorText};
  font-size: 14px;
  transition: all 0.2s;
  padding: 0;

  &:hover:not(:disabled) {
    border-color: ${({ token }) => token?.colorPrimary};
    color: ${({ token }) => token?.colorPrimary};
  }

  &:disabled {
    opacity: 0.5;
    color: ${({ token }) => token?.colorTextDisabled};
  }

  svg {
    color: inherit;
    fill: inherit;
  }

  &:disabled svg {
    color: ${({ token }) => token?.colorTextDisabled};
    fill: ${({ token }) => token?.colorTextDisabled};
  }
`;

export const ToolbarDivider = styled.div<IToken>`
  width: 1px;
  height: 24px;
  background: ${({ token }) => token.colorBorder};
  margin: 0 4px;
`;

export const ModeToggleButton = styled(Button)<IToken>`
  height: 32px;
  padding: 0 12px;
  font-size: 12px;
  color: ${({ token }) => token.colorText};

  svg {
    color: ${({ token }) => token.colorText};
    fill: ${({ token }) => token.colorText};
  }

  span {
    color: ${({ token }) => token.colorText};
  }
`;

export const EditorContentWrapper = styled.div<IToken>`
  flex: 1;
  overflow-y: auto;
  padding: 0;
  background: ${({ token }) => token.colorBgBase};
  min-height: 150px;
`;

export const StyledTextarea = styled.textarea<IToken & { scheme?: string }>`
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  padding: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ token, scheme }) => getDarkThemeTextColor(token, scheme)};
  background: ${({ token }) => token.colorBgBase};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  resize: none;
  min-height: 150px;

  &::placeholder {
    color: ${({ token }) => token.colorTextPlaceholder};
  }

  &:disabled {
    background: ${({ token }) => token.colorBgElevated};
    color: ${({ token }) => token.colorTextDisabled};
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
  }
`;

export const PreviewContainer = styled.div<IToken & { scheme?: string }>`
  padding: 12px;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  background: ${({ token }) => token.colorBgBase};
  color: ${({ token, scheme }) => getDarkThemeTextColor(token, scheme)};
  min-height: 150px;

  b,
  strong {
    font-weight: 600;
  }

  i,
  em {
    font-style: italic;
  }

  u {
    text-decoration: underline;
  }

  s {
    text-decoration: line-through;
  }

  a {
    color: ${({ token }) => token.colorPrimary};
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: ${({ token }) => token.colorPrimaryHover};
    }
  }

  ul,
  ol {
    margin: 8px 0;
    padding-left: 24px;
  }

  li {
    margin: 4px 0;
  }

  p {
    margin: 4px 0;
  }

  p.no-content {
    color: ${({ token }) => token.colorTextDisabled};
  }

  .text-left {
    text-align: left;
  }

  .text-center {
    text-align: center;
  }

  .text-right {
    text-align: right;
  }

  span[data-font-size] {
    font-size: attr(data-font-size px);
  }

  span.font-color[data-color],
  div.font-color[data-color] {
    color: var(--font-color);
  }

  span.bg-color[data-bg-color],
  div.bg-color[data-bg-color] {
    background-color: var(--bg-color);
    color: #000000;
  }
`;

export const ResizeHandle = styled.div<IToken>`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 20px;
  height: 20px;
  cursor: nwse-resize;
  background: linear-gradient(
    135deg,
    transparent 50%,
    ${({ token }) => token.colorBorder} 50%
  );
  border-radius: 0 0 4px 0;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;

  &:hover {
    opacity: 1;
  }
`;

export const ColorPresetContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ColorBox = styled.div<IToken & { color: string }>`
  width: 16px;
  height: 16px;
  background-color: ${({ color }) => color};
  border: 1px solid ${({ token }) => token?.colorBorder};
  border-radius: 2px;
`;
